import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { Box, Button, TextField } from "@mui/material";
import { MY_HR_VERIFY_2FA_TOTP_CODE } from "my-hr/graphql";
import { useMyHrUserByEmployee } from "my-hr/hooks";
import { useOcCurrentUserState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

interface MyHrLoginTwoFactorVerifyTotpCodeProps {
  onForward: () => void;
  onCancel: () => void;
  isRegister: boolean;
}

export const MyHrLoginTwoFactorVerifyTotpCode = ({
  onForward,
  onCancel,
  isRegister,
}: MyHrLoginTwoFactorVerifyTotpCodeProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [helperText, setHelperText] = useState("");
  const [disabled, setDisabled] = useState(false);
  const isMobile = useOcIsMobile();
  const previousHiddenRef = useRef(document.hidden);

  const { employeeCode } = useOcCurrentUserState();

  // Track document visibility changes - MOBILE RELOAD FIX
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasHidden = previousHiddenRef.current;
      const isNowHidden = document.hidden;

      // MOBILE FIX: If returning to tab (hidden: true → false), reload page
      // This fixes the Chrome bug where document.hidden gets stuck at true
      if (isMobile && wasHidden && !isNowHidden) {
        window.location.reload();
        return;
      }

      previousHiddenRef.current = isNowHidden;
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isMobile]);

  // Polling - MOBILE STUCK document.hidden DETECTION
  useEffect(() => {
    if (!isMobile) return;

    let lastUserInteraction = Date.now();

    // Track user interaction
    const handleInteraction = () => {
      lastUserInteraction = Date.now();
    };

    document.addEventListener("click", handleInteraction);
    document.addEventListener("input", handleInteraction);

    // Polling - checks every 2 seconds
    const intervalId = setInterval(() => {
      const timeSinceInteraction = Date.now() - lastUserInteraction;

      // If user interacted within 5 seconds AND document.hidden is stuck
      if (timeSinceInteraction < 5000 && document.hidden) {
        clearInterval(intervalId);
        window.location.reload();
      }
    }, 2000);

    // Cleanup: Stop polling when component unmounts
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("input", handleInteraction);
    };
  }, [isMobile]);
  const { refetch: refetchUserData } = useMyHrUserByEmployee({
    employee: employeeCode,
  });

  const [verify2faTotpCode] = useMutation(MY_HR_VERIFY_2FA_TOTP_CODE, {
    onCompleted: () => {
      onForward();
    },
    onError: async (error) => {
      // Refetch AFTER failed verification to get updated failedCode counter
      const { data: refreshedData } = await refetchUserData();
      const failedCode = refreshedData?.userByEmployee?.twoFactorFailedCode ?? 0;

      if (failedCode >= 2) {
        setDisabled(true);
      } else {
        setDisabled(false);
      }

      let errors = error?.graphQLErrors;
      errors.map((err) => {
        setHelperText(err?.message);
        return {
          message: err?.message,
          code: err?.extensions?.code,
        };
      });
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value;
    setCode(newCode);
  };

  const handleVerify = async () => {
    // CRITICAL FIX: Wait for tab to become visible before sending request
    // Chrome aborts fetch requests when tab is hidden!
    if (document.hidden) {
      await new Promise<void>((resolve) => {
        const handleVisibilityChange = () => {
          if (!document.hidden) {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            resolve();
          }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Safety timeout - if user doesn't return within 30 seconds, try anyway
        setTimeout(() => {
          document.removeEventListener("visibilitychange", handleVisibilityChange);
          resolve();
        }, 30000);
      });
    }

    verify2faTotpCode({
      variables: {
        code: code,
      },
    });
  };

  return (
    <>
      {" "}
      <Box sx={{ marginTop: "36px", marginBottom: "16px" }}>
        <TextField
          id="code"
          name="one-time-code"
          label={t("MY_HR_ENTER_AUTHENTICATION_CODE")}
          variant="outlined"
          fullWidth
          value={code}
          onChange={handleChange}
          error={!!helperText}
          helperText={t(helperText)}
          disabled={disabled}
          autoFocus
          autoComplete="one-time-code"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleVerify();
            }
          }}
        />
      </Box>
      <Button
        id="verification"
        variant="contained"
        color="primary"
        sx={{ marginTop: "24px" }}
        onClick={handleVerify}
        fullWidth
      >
        {t("MY_HR_VERIFICATION")}
      </Button>
      {!isRegister && (
        <>
          <Box height="36px" />
          <Button
            id="cancel"
            onClick={onCancel}
            color="secondary"
            variant="text"
            fullWidth
          >
            {t("OC_CANCEL")}
          </Button>
        </>
      )}
    </>
  );
};

export default MyHrLoginTwoFactorVerifyTotpCode;
